import { useState, useEffect } from 'react';

import styles from '../../styles';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import DashboardCardEditButton from '../../components/DashboardCardEditButton';
import CardDetails from '../../components/CardDetails';
import { Box, HStack, VStack, Heading, Text } from '@chakra-ui/react';
import { EditIcon, AddIcon, ViewIcon } from '@chakra-ui/icons';
import { Card, CardBody } from 'components/v4/Card';
import DashboardServiceCardSbcStatusbar from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardSbcStatusbar';
import { QUERY_CACHE_TIME, REFETCH_INTERVAL } from '../../constants';
import ServiceStatus from './ServiceStatus';
import getSipTrunkCardStatus from '../../components/helpers/sipTrunkStatus';

// Utils
import trunkStatusCodeToText from 'utils/trunkStatusCodeToText';
import Image from 'components/v4/Image';
import { MenuItem } from 'components/v4';

//APIs
import { useQuery } from 'hooks/useCustomQuery';
import getTrunks from 'API/Trunk/getTrunks';

// Assets
import { useDashboardMenu } from 'context/DashboardMenuContext';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';

export const SBCCardDetails = ({ service }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { darkMode } = useSelector((state) => state.settings);
    const { isQueryDisabled } = useDashboardMenu();
    const currentCompany = useSelector(
        (state) => state.navigationLists.currentCompany,
    );
    const accountScope = useSelector((state) => state.login.requiredScope);
    const data = service;

    // `/Trunk/Company/{id}` returns every trunk on the company, so the modern
    // service's trunks have to be excluded here or they are counted against
    // both cards.
    const { isSRTTrunk } = useSRTTrunkTypes();
    const { data: trunkData } = useQuery({
        queryKey: ['getTrunks'],
        queryFn: () => getTrunks(currentCompany),
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: REFETCH_INTERVAL,
        enabled: !isQueryDisabled,
    });

    const actionPermissions = {
        menuEditServiceScope: 40,
    };
    const [cardStatus, setCardStatus] = useState({
        sipCount: '...',
        channelCount: '...',
        status: '...',
        statusCode: null,
    });

    function calculateSbcStatus(trunkData) {
        let sipCount = 0;
        let channelCount = 0;
        let status = '';
        let statusCode = null;

        const classicTrunks = (trunkData?.data || []).filter(
            (trunk) => !isSRTTrunk(trunk),
        );

        classicTrunks.forEach((trunk) => {
            if (trunk.trunkType.sipEnabled) {
                sipCount++;
            }
            channelCount += trunk.channels;
        });

        const isOk = classicTrunks
            .map((trunk) => {
                if (trunk.status >= 0 && trunk.status <= 4) {
                    return trunk.status;
                }
            })
            .filter((n) => !Number(n) && typeof n !== 'undefined');

        const isWarning = classicTrunks
            .map((trunk) => {
                if (trunk.status === 8 || trunk.status === 9) {
                    return trunk.status;
                }
            })
            .filter((n) => !Number(n) && typeof n !== 'undefined');
        const isError = classicTrunks
            .map((trunk) => {
                if (trunk.status === 20) {
                    return trunk.status;
                }
            })
            .filter((n) => !Number(n) && typeof n !== 'undefined');

        if (isError.length) {
            statusCode = Math.max(...isError);
            status = trunkStatusCodeToText(statusCode);
        } else if (isWarning.length) {
            statusCode = Math.max(...isWarning);
            status = trunkStatusCodeToText(statusCode);
        } else if (isOk.length) {
            statusCode = Math.max(...isOk);
            status = trunkStatusCodeToText(statusCode);
        }

        setCardStatus({
            sipCount,
            channelCount,
            status,
            statusCode,
        });
    }

    useEffect(() => {
        if (trunkData) {
            setTimeout(() => {
                calculateSbcStatus(trunkData);
            }, 500);
        }
    }, [trunkData]);

    const addTrunks = () => {
        let tel = document.querySelector('.Resources');
        if (tel.children.length == 1) {
            var divElement = tel.querySelector('div');
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            divElement.dispatchEvent(clickEvent);
        }
        dispatch({
            type: 'REDIRECT',
            payload: {
                pathname: '/resources/trunks',
                scope: 20,
            },
        });
        navigate('/trunks/add');
    };

    const viewTrunks = () => {
        let tel = document.querySelector('.Resources');
        if (tel.children.length == 1) {
            var divElement = tel.querySelector('div');
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            divElement.dispatchEvent(clickEvent);
        }

        dispatch({
            type: 'REDIRECT',
            payload: {
                pathname: '/resources/trunks',
                scope: 20,
            },
        });
        navigate('/trunks');
    };

    // (3134) Degraded and Error name the trunk status rather than leaving
    // the pill blank; the collapsed header's badge reads the same.
    const pillStatus = getSipTrunkCardStatus(
        cardStatus.statusCode ?? service?.statusDisplay?.statusDisplayLevel,
    );

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                minWidth: '250px',
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <DashboardServiceCardSbcStatusbar
                sx={styles.verticalBar}
                status={
                    cardStatus.statusCode ??
                    service?.statusDisplay?.statusDisplayLevel
                }
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                }}>
                <VStack
                    alignItems="flex-start"
                    width="100%"
                    spacing={2}
                    marginBottom="15px">
                    <HStack
                        alignItems="flex-start"
                        width="100%"
                        overflow="hidden">
                        <Image
                            src={CardDetails(service?.serviceType)?.icon}
                            sx={{ maxWidth: '50px', flexShrink: 0 }}
                        />
                        <VStack alignItems="flex-start" width="100%" minW={0}>
                            <Heading as="h4" size="md">
                                {CardDetails(service?.serviceType)?.serviceName}
                            </Heading>
                            {/* Status */}
                            <Box
                                className="flex justify-between"
                                sx={{ paddingBottom: '20px', width: '100%' }}>
                                <ServiceStatus
                                    service={service}
                                    cardStatus={{
                                        ...cardStatus,
                                        ...pillStatus,
                                    }}
                                    // handleActions={{ handleGrantAccess }}
                                />
                            </Box>
                            {/* SIP Trunks */}
                            <Box
                                className="mb-[15px] flex justify-between"
                                style={{ width: '60%' }}>
                                <Text className="font-medium">SIP Trunks:</Text>
                                <Text margin={0}>{cardStatus.sipCount}</Text>
                            </Box>

                            {/* Channels */}
                            <Box
                                className="mb-[15px] flex justify-between"
                                style={{ width: '60%' }}>
                                <Text className="font-medium">Channels:</Text>
                                <Text margin={0}>
                                    {cardStatus.channelCount}
                                </Text>
                            </Box>
                        </VStack>
                    </HStack>
                </VStack>
                <DashboardCardEditButton>
                    <MenuItem
                        icon={<AddIcon />}
                        onClick={addTrunks}
                        data-walkthroughid={`/dashboard-service/card/${service.name}/add-trunk`}>
                        <Text marginBottom="0">Add Trunk</Text>
                    </MenuItem>
                    <MenuItem icon={<ViewIcon />} onClick={viewTrunks}>
                        <Text marginBottom="0">View Trunks</Text>
                    </MenuItem>
                    {accountScope >= actionPermissions.menuEditServiceScope && (
                        <Link
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/sbcaas/${data.id}/edit`}
                            state={{
                                serviceId: data.id,
                                data,
                            }}>
                            <MenuItem
                                icon={<EditIcon />}
                                data-walkthroughid={`/dashboard-service/card/${service.name}/edit-trunk`}>
                                <Text marginBottom="0">Edit Service</Text>
                            </MenuItem>
                        </Link>
                    )}
                </DashboardCardEditButton>
            </CardBody>
        </Card>
    );
};

export default SBCCardDetails;
